import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Privé", to: "/prive" },
  { label: "Collective", to: "/collective" },
  { label: "Destinations", to: "/destinations" },
  { label: "Services", to: "/#services" },
  { label: "About", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (pathname !== "/") { navigate("/"); setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 400); }
      else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else navigate(to);
  };

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2.7, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 right-0 z-[60] transition-[background-color,backdrop-filter,border-color] duration-500"
        style={{
          backgroundColor: scrolled ? "rgba(13,16,56,0.85)" : "transparent",
          backdropFilter: scrolled ? "blur(18px) saturate(140%)" : "none",
          borderBottom: scrolled ? "1px solid rgba(212,175,55,0.25)" : "1px solid transparent",
        }}
      >
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
          <button onClick={() => handleNav("/")} data-testid="nav-logo" className="flex items-center">
            <Logo />
          </button>
          <nav className="hidden lg:flex items-center gap-10">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-${l.label.toLowerCase()}`}
                onClick={() => handleNav(l.to)}
                className="overline text-ivory/85 hover:text-gold transition-colors link-underline"
              >
                {l.label}
              </button>
            ))}
            <Link to="/enquire" data-testid="nav-enquire" className="btn-gold !py-2.5 !px-6">Enquire</Link>
          </nav>
          <button className="lg:hidden text-ivory" onClick={() => setOpen(true)} data-testid="nav-menu-open" aria-label="Open menu">
            <Menu size={26} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[80] bg-navy grain flex flex-col"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu"
          >
            <div className="h-[76px] px-6 flex items-center justify-between">
              <Logo />
              <button onClick={() => setOpen(false)} className="text-ivory" data-testid="nav-menu-close" aria-label="Close menu"><X size={26} /></button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-6 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                  onClick={() => handleNav(l.to)}
                  className="font-serif text-5xl text-ivory text-left hover:text-gold transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <Link to="/enquire" onClick={() => setOpen(false)} className="btn-gold mt-6 w-fit">Enquire</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
